// Device-local Explain preferences: narration, and which solution layout the
// student last chose.
//
// These are device-local by nature: a SpeechSynthesis voice URI from Chrome on
// Windows is meaningless in Safari on an iPad, and a mute pressed on the family
// tablet should not have to round-trip through Firestore to take effect. They
// live in localStorage, mirroring the `quillnote-theme` pattern in
// src/contexts/ThemeContext.js.
//
// Every key is scoped to the student being taught rather than to the signed-in
// account. A parent in Student View is the same account for two different
// children on the same browser, so an unscoped key would let one sibling
// inherit the other's mute setting and voice.

const NARRATION_KEY_PREFIX = 'quillnote-explain-narration'
const VOICE_KEY_PREFIX = 'quillnote-explain-voice'
const SOLUTION_VIEW_KEY_PREFIX = 'quillnote-explain-solution-view'

const ENABLED = 'on'
const DISABLED = 'off'

// How the live Explain lesson draws steps the student has already reached.
// Cards match View Work (a bordered step with its explanation). Worksheet is
// the continuous solution. Cards are the default for a student with no pick.
export const EXPLAIN_SOLUTION_VIEW_CARDS = 'cards'
export const EXPLAIN_SOLUTION_VIEW_WORKSHEET = 'worksheet'

const SOLUTION_VIEWS = new Set([
  EXPLAIN_SOLUTION_VIEW_CARDS,
  EXPLAIN_SOLUTION_VIEW_WORKSHEET
])

// An empty key means "no student to scope to" (signed out, or the profile has
// not resolved yet), which reads as the default and writes nothing.
const scopedKey = (prefix, userId) => (
  typeof userId === 'string' && userId.trim() ? `${prefix}-${userId.trim()}` : ''
)

const readItem = (key) => {
  if (!key || typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private mode or storage disabled by policy: the caller's default applies.
    return null
  }
}

const writeItem = (key, value) => {
  if (!key || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Quota or private mode: the in-memory preference still holds for this tab.
  }
}

/**
 * @param {string} userId Student the narration belongs to.
 * @param {boolean} fallback Applied only when this student has nothing stored,
 *   which is what lets an in-flight session's `ttsEnabled` still be honoured.
 * @return {boolean} True when narration should play; false means muted.
 */
export const readExplainNarrationEnabled = (userId, fallback = false) => {
  const stored = readItem(scopedKey(NARRATION_KEY_PREFIX, userId))
  if (stored === ENABLED) return true
  if (stored === DISABLED) return false
  return fallback === true
}

export const persistExplainNarrationEnabled = (userId, enabled) => {
  writeItem(scopedKey(NARRATION_KEY_PREFIX, userId), enabled ? ENABLED : DISABLED)
}

/**
 * @return {string} The student's saved voice URI, or '' to let the narrator
 *   resolve a sensible default for whatever voices this device offers.
 */
export const readExplainNarrationVoiceUri = (userId) => {
  const stored = readItem(scopedKey(VOICE_KEY_PREFIX, userId))
  return typeof stored === 'string' && stored.trim() ? stored.trim() : ''
}

export const persistExplainNarrationVoiceUri = (userId, voiceUri) => {
  if (typeof voiceUri !== 'string' || !voiceUri.trim()) return
  writeItem(scopedKey(VOICE_KEY_PREFIX, userId), voiceUri.trim())
}

/**
 * @param {string} userId Student the lesson layout belongs to.
 * @return {'cards'|'worksheet'} Cards when this student has no stored choice.
 */
export const readExplainSolutionView = (userId) => {
  const stored = readItem(scopedKey(SOLUTION_VIEW_KEY_PREFIX, userId))
  return SOLUTION_VIEWS.has(stored) ? stored : EXPLAIN_SOLUTION_VIEW_CARDS
}

export const persistExplainSolutionView = (userId, view) => {
  if (!SOLUTION_VIEWS.has(view)) return
  writeItem(scopedKey(SOLUTION_VIEW_KEY_PREFIX, userId), view)
}
